/**
 * The session trace: what actually happened, on by default, durable across page death.
 *
 * This exists because of a specific failure. Someone sat through a thirty-minute session
 * on a phone and reported five distinct defects afterwards — a hole, a level step, a cut
 * word, artefacts at the end — and nothing in the app knew anything about it. There was
 * no record of which ops were scheduled, what the context's sample rate was, which
 * container was chosen, or whether a schedule had been late. Every one of those questions
 * had to be answered by reasoning from the source, and two of the answers were wrong.
 *
 * So the rule here is: **an anomaly is a named record, never a silent clamp.** Where the
 * engine used to quietly repair something — a `when` already in the past, a fill starting
 * while another was in flight, a buffer shorter than the op that reads it — it now says
 * so, with both clocks and enough detail to find the op again.
 *
 * Three properties matter more than completeness:
 *
 *   * **On by default.** A diagnostic you have to turn on is off when the bug happens.
 *     `src/diagnostics.ts` uploads each sit by default, including the recording title,
 *     recording ID, and full user agent, and Settings provides the switch that stops it.
 *     The recorder remains separate from that delivery policy so neither job can hide a
 *     failure in the other.
 *   * **It survives the sit.** The report came in after the session ended, which is when
 *     an in-memory buffer is already gone. The trace is written to localStorage as it
 *     goes and the last few sessions are kept, so "it sounded wrong around minute twelve"
 *     is still answerable an hour later.
 *   * **Clock domains stay named.** Browser audio, native renderer position and
 *     `performance.now()` answer different questions. Their domains and observation
 *     uncertainty travel with the value so process uptime cannot masquerade as playback.
 *
 * Cost is bounded by construction: a sixty-minute session at the busiest cadence
 * schedules on the order of 120 ops, so a full trace is tens of kilobytes. The ring
 * buffer is there for the pathological case — an anomaly that fires every tick for an
 * hour — and counts what it drops rather than pretending it kept everything.
 */

/** Entries beyond this are dropped and counted. ~10x the busiest real session. */
const MAX_ENTRIES = 4000;
/** How many finished sessions to keep on the device. */
const MAX_SESSIONS = 3;
/** localStorage is synchronous; do not touch it more often than this during a sit. */
const PERSIST_THROTTLE_MS = 10_000;
/** Native status polls four times a second; a trace sample every five seconds is enough. */
const CLOCK_LOG_THROTTLE_MS = 5_000;

const KEY = 'brahm.trace.v1';

/**
 * The anomalies worth a name. A string union rather than free text, so the debug screen
 * and any future gate can count them without parsing prose.
 */
export type AnomalyKind =
  /** A source was scheduled with a `when` already in the past, and had to be clamped. */
  | 'late_schedule'
  /**
   * The native service refused `play` as having nothing loaded, after `load()` had already
   * resolved, and was asked again. The sit recovers; the controller being out of step with
   * its own service is the thing worth counting. Two sits were refused outright on
   * 2026-09-14 before the retry existed.
   */
  | 'play_retry'
  /** Audio is scheduled less than 5 s ahead of the playhead — the next stall is close. */
  | 'horizon_underrun'
  /** A fill began while another was still in flight. This once double-scheduled audio. */
  | 'fill_reentry'
  /** decodeAudioData returned a length more than 20 ms off what the manifest claims. */
  | 'decode_mismatch'
  /** An op wanted more audio than its buffer holds and was cut short. */
  | 'buffer_short'
  /** The AudioContext changed state — suspended by the OS, interrupted by a call. */
  | 'ctx_state'
  /**
   * The page was hidden or shown. Backgrounding is when iOS throttles timers.
   * event=session-download-incomplete also records missing compressed parts/bytes.
   */
  | 'visibility'
  /** Freeze, resume, pagehide, pageshow, or evidence left by a later page instance. */
  | 'page_lifecycle'
  /** The audio clock and the wall clock disagree by more than a tick. */
  | 'clock_drift'
  /** Assembly could not keep up and playback stopped. */
  | 'stalled'
  /** A QA measurement on rendered tone came back outside its bound. */
  | 'qa_fail'
  /** Anything that threw where a throw is not fatal. */
  | 'error';

export interface TraceEntry {
  seq: number;
  /** Civil time for matching a frozen interval to what happened outside the page. */
  wallMs: number;
  /** Legacy numeric clock column; use `clock` to know what the value means. */
  ctxSec: number | null;
  /** The clock that produced `ctxSec`; absent entries predate explicit clock domains. */
  clock?: TraceClockReading;
  /** `performance.now()`, milliseconds since page load. */
  perfMs: number;
  kind: string;
  anomaly?: true;
  detail?: Record<string, unknown>;
}

export type TraceClockDomain = 'web_audio' | 'native_output' | 'performance';

export interface TraceClockReading {
  domain: TraceClockDomain;
  valueSec: number;
  /** Bound introduced by polling/transport. Zero is valid for a synchronous web clock. */
  uncertaintyMs: number;
  playbackFrame?: number;
  sampleRate?: number;
  /** Timestamp in the clock's own monotonic domain, when the renderer was observed. */
  observedAtMs?: number;
}

export interface SessionTrace {
  id: string;
  /** Wall clock at open, so a trace can be matched to "the sit I did before lunch". */
  startedAt: number;
  endedAt?: number;
  header: Record<string, unknown>;
  entries: TraceEntry[];
  /** Entries the ring buffer discarded. Non-zero is itself a finding. */
  dropped: number;
  anomalies: Partial<Record<AnomalyKind, number>>;
}

type ClockFn = () => number | TraceClockReading | null;

export interface DutySummary {
  wallSec: number;
  audioSec: number;
  /** Audio-clock progress as a whole percentage of wall-clock progress. */
  dutyPct: number;
  /**
   * Which clock the wall span came from, because they are not equally trustworthy.
   * `wall` is `Date.now()`, which a clock change can move. `perf` is
   * `performance.now()`, which browsers differ on across OS sleep — exactly the
   * interval a stalled sit spends asleep. A duty derived from `perf` is a floor on
   * how bad things were, not a measurement, and the screen must say which it is.
   */
  basis: 'wall' | 'perf';
  clockDomain?: 'web_audio' | 'native_output' | 'legacy_inferred';
  uncertaintySec?: number;
}

/**
 * Summarise how much of an observed wall-clock span a renderer clock actually ran for.
 *
 * Wall time is derived from `wallMs`, not `perfMs`: the phone trace that motivated this
 * survived OS sleep, while `performance.now()` restarted with each replacement page.
 * A performance-domain value is deliberately rejected: it can order process events but
 * cannot prove that an audio renderer advanced. Civil time can jump when the device clock
 * changes, so a non-positive span is rejected;
 * the surviving figures are rounded to seconds and a whole percent rather than implying
 * that either clock explains sub-second playback precisely.
 */
export function dutySummary(entries: TraceEntry[]): DutySummary | null {
  // Both clocks must come from the SAME entry. Filtering the two independently is how
  // this first shipped, and against the real 30 Aug trace off Guy's phone it reported
  // 1% duty for a sit that ran at roughly half: that trace predates `wallMs`, so its own
  // entries carried only `ctxSec`, while the three `page_lifecycle` rows appended by
  // later page loads carried only `wallMs` — stamped nine hours afterwards. The function
  // divided one session's audio span by another day's wall span and returned a confident
  // number. A ratio between two clocks is meaningless unless both were read at the same
  // instant.
  const explicit = entries.filter((e) => e.clock
    && (e.clock.domain === 'web_audio' || e.clock.domain === 'native_output'));
  const explicitDomain = explicit[0]?.clock?.domain;
  const compatible = explicitDomain
    ? explicit.filter((e) => e.clock?.domain === explicitDomain)
    : entries.filter((e) => !e.clock && e.ctxSec !== null && Number.isFinite(e.ctxSec));
  const paired = (read: (e: TraceEntry) => number | null | undefined) => compatible
    .filter((e) => Number.isFinite(e.clock?.valueSec ?? e.ctxSec) && Number.isFinite(read(e)))
    .map((e) => ({
      audio: (e.clock?.valueSec ?? e.ctxSec) as number,
      span: read(e) as number,
      uncertaintyMs: e.clock?.uncertaintyMs ?? 0,
    }));

  // Prefer civil time; fall back to the monotonic clock only so that traces written
  // before `wallMs` existed still say something, clearly labelled.
  let basis: 'wall' | 'perf' = 'wall';
  let rows = paired((e) => e.wallMs);
  if (rows.length < 2) {
    basis = 'perf';
    rows = paired((e) => e.perfMs);
  }
  if (rows.length < 2) return null;

  const wallSec = (rows.at(-1)!.span - rows[0]!.span) / 1000;
  const audioSec = rows.at(-1)!.audio - rows[0]!.audio;
  if (wallSec <= 0 || audioSec < 0) return null;
  const result: DutySummary = {
    wallSec: Math.round(wallSec),
    audioSec: Math.round(audioSec),
    dutyPct: Math.round((audioSec / wallSec) * 100),
    basis,
  };
  if (explicitDomain) {
    result.clockDomain = explicitDomain as 'web_audio' | 'native_output';
    result.uncertaintySec = (rows[0].uncertaintyMs + rows.at(-1)!.uncertaintyMs) / 1000;
  } else {
    // Old exports never identified what `ctxSec` meant. It was normally AudioContext
    // time, but native builds briefly wrote performance.now() here, so retain the useful
    // historical estimate without presenting its domain as observed fact.
    result.clockDomain = 'legacy_inferred';
  }
  return result;
}

/** A repeated statechange or a return to running is evidence, but not a fault. */
export function contextStateIsAnomaly(previous: string, next: string): boolean {
  return previous !== next && next !== 'running';
}

/**
 * Storage may be absent (private mode), and it may also be *present but not a Storage*.
 * Under vitest on Node 25 the global `localStorage` is an object with no `getItem` and no
 * `setItem` at all — which means `settings.ts` and `history.ts` have been persisting into
 * nothing in every test that has ever run here, silently, because their writes are inside
 * a try/catch. Duck-type the methods rather than trusting the global to exist.
 */
function storage(): Storage | null {
  try {
    const s: unknown = typeof localStorage === 'undefined' ? null : localStorage;
    if (!s || typeof (s as Storage).getItem !== 'function'
        || typeof (s as Storage).setItem !== 'function') {
      return null;
    }
    return s as Storage;
  } catch {
    return null;
  }
}

function now(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now();
}

export class Trace {
  private current: SessionTrace | null = null;
  private seq = 0;
  private clock: ClockFn = () => null;
  // -Infinity, not 0: `performance.now()` starts near zero, so a throttle measured from 0
  // suppresses the FIRST write for the first ten seconds of the page's life — which is
  // exactly the window in which an early failure is most likely and the trace most needed.
  // Caught by an e2e test that played a sit for 1.5 s and found nothing stored.
  private lastPersist = -Infinity;
  private pending = false;
  private lastClockLogPerfMs = -Infinity;

  /** Where the entry's explicit clock sample comes from. */
  setClock(fn: ClockFn): void {
    this.clock = fn;
  }

  /**
   * Install a renderer observation as the clock for subsequent entries and periodically
   * retain it in its own row. Native status arrives four times a second; retaining every
   * poll would exhaust the bounded trace before a normal sit ended.
   */
  observeClock(reading: TraceClockReading): void {
    this.clock = () => reading;
    const perfMs = now();
    if (perfMs - this.lastClockLogPerfMs < CLOCK_LOG_THROTTLE_MS) return;
    this.lastClockLogPerfMs = perfMs;
    this.log('playback_position');
  }

  /**
   * Begin a session trace. Any session already open is closed first rather than merged —
   * two sessions in one trace would make every "when did this happen" answer ambiguous.
   */
  open(id: string, header: Record<string, unknown>): void {
    if (this.current) this.close({ reason: 'superseded' });
    this.seq = 0;
    this.lastClockLogPerfMs = -Infinity;
    this.current = {
      id,
      startedAt: Date.now(),
      header,
      entries: [],
      dropped: 0,
      anomalies: {},
    };
    this.log('session_open', header);
  }

  /** Add to the open session's header after the fact — the container probe finishes late. */
  amendHeader(patch: Record<string, unknown>): void {
    if (!this.current) return;
    this.current.header = { ...this.current.header, ...patch };
    // Header amendments contain facts which arrive asynchronously (notably the wake-lock
    // result). A phone killed immediately afterwards must not retain the old "pending".
    this.persist(true);
  }

  log(kind: string, detail?: Record<string, unknown>): void {
    this.push({ kind, detail });
  }

  anomaly(kind: AnomalyKind, detail?: Record<string, unknown>): void {
    this.push({ kind, detail, anomaly: true });
    if (!this.current) return;
    this.current.anomalies[kind] = (this.current.anomalies[kind] ?? 0) + 1;
    // An anomaly is the thing worth having if the page dies in the next second.
    this.persist(true);
  }

  private push(e: { kind: string; detail?: Record<string, unknown>; anomaly?: true }): void {
    const t = this.current;
    if (!t) return;
    if (t.entries.length >= MAX_ENTRIES) {
      // Drop the oldest ordinary entry rather than the newest of anything: the tail is
      // where a session goes wrong, and this project has an open defect about the end.
      const i = t.entries.findIndex((x) => !x.anomaly);
      if (i < 0) { t.dropped++; return; }
      t.entries.splice(i, 1);
      t.dropped++;
    }
    const reading = this.clock();
    const clock = typeof reading === 'number'
      ? { domain: 'web_audio' as const, valueSec: reading, uncertaintyMs: 0 }
      : reading;
    t.entries.push({
      seq: this.seq++,
      wallMs: Date.now(),
      ctxSec: clock?.valueSec ?? null,
      ...(clock ? { clock } : {}),
      perfMs: Math.round(now() * 10) / 10,
      kind: e.kind,
      ...(e.anomaly ? { anomaly: true as const } : {}),
      ...(e.detail ? { detail: e.detail } : {}),
    });
    this.persist(false);
  }

  close(detail?: Record<string, unknown>): void {
    if (!this.current) return;
    this.log('session_close', detail);
    const closeEntry = this.current.entries.at(-1);
    const duty = dutySummary(this.current.entries);
    if (closeEntry?.kind === 'session_close' && duty) {
      closeEntry.detail = { ...closeEntry.detail, ...duty };
    }
    this.current.endedAt = Date.now();
    const done = this.current;
    this.current = null;
    this.write([done, ...this.recent().filter((s) => s.id !== done.id)].slice(0, MAX_SESSIONS));
  }

  /** The session in progress, if any. */
  snapshot(): SessionTrace | null {
    return this.current;
  }

  /** Finished sessions, newest first. */
  recent(): SessionTrace[] {
    const s = storage();
    if (!s) return [];
    try {
      const raw = s.getItem(KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? (parsed as SessionTrace[]) : [];
    } catch {
      return [];
    }
  }

  /**
   * Every sit the device holds, newest first, the open one included and counted once.
   *
   * The dedupe is the whole reason this exists. `persist()` writes the open session
   * through to storage as it goes, so `recent()` already contains it, and
   * `[current, ...recent()]` therefore listed the sit in progress TWICE — in the export
   * and, with a duplicated Preact key, on the debug screen. A reader counting sits in a
   * dump would have counted one too many, which is the last thing a diagnostic should do.
   */
  sessions(): SessionTrace[] {
    const finished = this.recent();
    if (!this.current) return finished;
    const live = this.current;
    return [live, ...finished.filter((s) => s.id !== live.id)];
  }

  /** Everything the device holds, as the file the debug screen exports. */
  exportJson(): string {
    const sessions = this.sessions();
    return JSON.stringify(
      {
        exportedAt: new Date().toISOString(),
        userAgent: typeof navigator === 'undefined' ? null : navigator.userAgent,
        sessions,
      },
      null,
      2,
    );
  }

  clear(): void {
    this.current = null;
    const s = storage();
    try { s?.removeItem(KEY); } catch { /* private mode */ }
  }

  /**
   * Write the open session through so a trace survives the page being killed. Throttled,
   * because localStorage is synchronous and a sit must not be janked by its own
   * diagnostics; `force` is for anomalies and for pagehide, where there may be no later.
   */
  private persist(force: boolean): void {
    if (!this.current) return;
    const t = now();
    if (!force && t - this.lastPersist < PERSIST_THROTTLE_MS) { this.pending = true; return; }
    this.lastPersist = t;
    this.pending = false;
    const live = this.current;
    this.write([live, ...this.recent().filter((s) => s.id !== live.id)].slice(0, MAX_SESSIONS));
  }

  /** Called from pagehide, where a throttled write would otherwise be lost. */
  flush(): void {
    if (this.pending) this.persist(true);
  }

  /**
   * Join a page which died without a final callback to the next page instance.
   *
   * Chrome exposes discard only after loading the replacement page. Closing the old sit
   * here would be dishonest—it still did not end cleanly—so the stored trace remains
   * open and gains the evidence supplied by the next load.
   */
  noteNextPage(detail: Record<string, unknown>): void {
    const sessions = this.recent();
    const previous = sessions.find((session) => session.endedAt === undefined);
    if (!previous) return;
    // Stop at the predecessor's last own entry. Including this next-page load would count
    // the hours after a killed page as sitting time, which is the opposite of the fact the
    // annotation is meant to preserve.
    const duty = dutySummary(previous.entries);
    const perfMs = Math.round(now() * 10) / 10;
    previous.entries.push({
      seq: previous.entries.reduce((max, entry) => Math.max(max, entry.seq), -1) + 1,
      wallMs: Date.now(),
      ctxSec: null,
      perfMs,
      kind: 'page_lifecycle',
      anomaly: true,
      detail: { event: 'next-page-load', ...detail, ...(duty ?? {}) },
    });
    previous.anomalies.page_lifecycle = (previous.anomalies.page_lifecycle ?? 0) + 1;
    previous.header = { ...previous.header, nextPage: detail };
    this.write(sessions);
  }

  private write(sessions: SessionTrace[]): void {
    const s = storage();
    if (!s) return;
    try {
      s.setItem(KEY, JSON.stringify(sessions));
    } catch {
      // Quota. Keep the newest session rather than losing the trace entirely; if even
      // that will not fit, give up silently — diagnostics never break a sit.
      try { s.setItem(KEY, JSON.stringify(sessions.slice(0, 1))); } catch { /* give up */ }
    }
  }
}

export const trace = new Trace();

/** Total anomalies by kind across a set of traces, for the debug screen and the beacon. */
export function anomalyTotals(sessions: SessionTrace[]): Partial<Record<AnomalyKind, number>> {
  const out: Partial<Record<AnomalyKind, number>> = {};
  for (const s of sessions) {
    for (const [k, n] of Object.entries(s.anomalies)) {
      const key = k as AnomalyKind;
      out[key] = (out[key] ?? 0) + (n ?? 0);
    }
  }
  return out;
}

/** What a dump carries besides the sits themselves. The device it came from is the point. */
export interface TraceExport {
  exportedAt: string | null;
  userAgent: string | null;
  sessions: SessionTrace[];
}

/**
 * Read a dump back in.
 *
 * `exportJson` has existed since the trace did, and nothing could ever open what it
 * wrote. A diagnostic that can be copied out but not read back is only half a
 * diagnostic: the sit that goes wrong happens on a phone, and the person who can read
 * the trace is at a desk. This is the other half.
 *
 * It validates rather than casts. The reason is the same one that motivates the trace
 * itself — a dump arrives from a device we do not have, possibly through a chat client
 * that mangled it, and a silently-wrong render of a bad file would send someone hunting
 * a defect that is really a truncated paste. Anything that is not a session is dropped
 * and counted, and a file with no sessions at all throws rather than showing an empty
 * screen that looks like a clean device.
 *
 * A bare array is accepted as well as the envelope, because the other way a dump reaches
 * a desk is someone reading `brahm.trace.v1` straight out of devtools.
 */
export function parseExport(text: string): TraceExport & { skipped: number } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    throw new Error(`that is not JSON — ${(e as Error).message}`);
  }
  const envelope = Array.isArray(parsed)
    ? { sessions: parsed, exportedAt: null, userAgent: null }
    : (parsed as Partial<TraceExport>);
  const raw: unknown = envelope?.sessions;
  if (!Array.isArray(raw)) {
    throw new Error('no "sessions" array — is this a diagnostics file?');
  }
  const sessions = raw.filter(isSession);
  if (sessions.length === 0) {
    throw new Error(`no readable sits in ${raw.length} record${raw.length === 1 ? '' : 's'}`);
  }
  return {
    exportedAt: typeof envelope.exportedAt === 'string' ? envelope.exportedAt : null,
    userAgent: typeof envelope.userAgent === 'string' ? envelope.userAgent : null,
    sessions,
    skipped: raw.length - sessions.length,
  };
}

function isSession(v: unknown): v is SessionTrace {
  if (!v || typeof v !== 'object') return false;
  const s = v as Partial<SessionTrace>;
  return typeof s.id === 'string'
    && typeof s.startedAt === 'number'
    && Array.isArray(s.entries)
    && !!s.header && typeof s.header === 'object'
    && !!s.anomalies && typeof s.anomalies === 'object';
}
